import React from "react";
import { getTechInfo, getTierTechBadges } from "./TechStackIcons";

function VerticalConnector({ connector }) {
  const { label, dir = "down", dashed = false, accent = false } = connector;
  const showUp = dir === "up" || dir === "both";
  const showDown = dir === "down" || dir === "both";

  return (
    <div className={`px-arch-v-arrow ${accent ? "is-accent" : ""} ${dashed ? "is-dashed" : ""}`}>
      <svg viewBox="0 0 24 56" aria-hidden="true">
        <line
          x1="12"
          y1={showUp ? "7" : "2"}
          x2="12"
          y2={showDown ? "49" : "54"}
          strokeDasharray={dashed ? "3 3" : undefined}
        />
        {showUp && <polyline points="7,12 12,5 17,12" />}
        {showDown && <polyline points="7,44 12,51 17,44" />}
      </svg>
      <span>{label}</span>
    </div>
  );
}

export default function ProjectArchitecture({ project }) {
  const blueprint = project.caseStudy.blueprint;
  const tiers = blueprint?.tiers || [];
  const connectors = blueprint?.connectors || [];

  if (!tiers.length) return null;

  return (
    <section className="px-system px-arch" aria-label="System architecture blueprint" data-enter>
      <div className="px-arch-head">
        <div>
          <p className="st-eyebrow">ARCHITECTURE OVERVIEW</p>
          <h2>High-level system architecture.</h2>
        </div>
        <span className="px-arch-topology">{blueprint.topology}</span>
      </div>

      <div className="px-arch-board">
        {tiers.map((tier, tierIndex) => {
          const tierBadges = getTierTechBadges(tier.label);

          return (
            <React.Fragment key={tier.label}>
              <div className="px-arch-subgraph" role="group" aria-label={tier.label}>
                <div className="px-arch-subgraph-head">
                  <div className="px-arch-subgraph-meta">
                    <span>0{tierIndex + 1}</span>
                    <strong>{tier.label}</strong>
                  </div>
                  {tierBadges.length > 0 && (
                    <div className="px-arch-tier-badges" aria-hidden="true">
                      {tierBadges.map((badge) => {
                        const Icon = badge.Icon;
                        return (
                          <span
                            key={badge.label}
                            className="px-arch-tier-badge"
                            title={badge.label}
                            style={{ "--tech-color": badge.color }}
                          >
                            <Icon />
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="px-arch-subgraph-nodes">
                  {tier.nodes.map((node, nodeIndex) => {
                    const techInfo = getTechInfo(node, tier.label);
                    const TechIcon = techInfo.Icon;

                    return (
                      <React.Fragment key={node.title}>
                        <div
                          className={`px-arch-box ${node.primary ? "is-primary" : ""}`}
                          style={{ "--tech-color": techInfo.color }}
                        >
                          <div className="px-arch-box-top">
                            <span className="px-arch-tech-badge">
                              <TechIcon className="px-arch-tech-svg" aria-hidden="true" />
                              <span className="px-arch-tech-name">{techInfo.label}</span>
                            </span>
                            {node.primary && <span className="px-arch-core-pill">CORE</span>}
                          </div>

                          <div className="px-arch-box-content">
                            <strong>{node.title}</strong>
                            <small>{node.sub}</small>
                          </div>
                        </div>

                        {nodeIndex < tier.nodes.length - 1 && (
                          <div
                            className={`px-arch-h-arrow ${node.edgeToNext ? "has-edge" : "is-spacer"} ${
                              node.edgeToNext?.dashed ? "is-dashed" : ""
                            }`}
                            aria-hidden="true"
                          >
                            {node.edgeToNext && (
                              <>
                                {node.edgeToNext.label && <em>{node.edgeToNext.label}</em>}
                                <svg viewBox="0 0 64 18" preserveAspectRatio="none">
                                  <line
                                    x1={node.edgeToNext.dir === "both" ? "7" : "2"}
                                    y1="9"
                                    x2="57"
                                    y2="9"
                                    strokeDasharray={node.edgeToNext.dashed ? "3 3" : undefined}
                                    vectorEffect="non-scaling-stroke"
                                  />
                                  {node.edgeToNext.dir === "both" && (
                                    <polyline points="11,5 5,9 11,13" vectorEffect="non-scaling-stroke" />
                                  )}
                                  <polyline points="53,5 59,9 53,13" vectorEffect="non-scaling-stroke" />
                                </svg>
                              </>
                            )}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>

              {tierIndex < tiers.length - 1 && connectors[tierIndex] && (
                <div className="px-arch-v-row" aria-label={`Connections from ${tier.label}`}>
                  {connectors[tierIndex].map((connector) => (
                    <VerticalConnector key={connector.label} connector={connector} />
                  ))}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </section>
  );
}
